import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '@/store/auth.store';
import { authApi, ApiError } from '@/lib/api-client';
import { Button, Input, Label, Spinner } from '@/components/ui';
import { AuthBrandPanel } from '@/components/AuthBrandPanel';

const schema = z.object({
  email: z.string().email('올바른 이메일을 입력해 주세요.'),
  password: z.string().min(8, '비밀번호는 8자 이상이어야 합니다.'),
});
type FormValues = z.infer<typeof schema>;

const OAUTH: { provider: 'kakao' | 'google' | 'naver'; label: string; cls: string }[] = [
  { provider: 'kakao', label: '카카오로 로그인', cls: 'bg-[#FEE500] text-black hover:bg-[#FEE500]/90' },
  { provider: 'google', label: 'Google로 로그인', cls: 'bg-white text-black border border-input hover:bg-accent' },
  { provider: 'naver', label: '네이버로 로그인', cls: 'bg-[#03C75A] text-white hover:bg-[#03C75A]/90' },
];

export default function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get('redirect') ?? '/';
  const login = useAuthStore((s) => s.login);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  async function onSubmit(values: FormValues) {
    setFormError(null);
    try {
      await login(values.email, values.password);
      navigate(redirect, { replace: true });
    } catch (err) {
      setFormError(
        err instanceof ApiError
          ? err.message
          : '로그인에 실패했습니다. 다시 시도해 주세요.',
      );
    }
  }

  return (
    <div className="grid min-h-[calc(100vh-4rem)] lg:grid-cols-2">
      <AuthBrandPanel />
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
        <h1 className="text-2xl font-bold tracking-tight">다시 오신 걸 환영해요</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          로그인하고 우리 동네 공동구매에 참여하세요.
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4" noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="email">이메일</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              {...register('email')}
            />
            {errors.email && (
              <p className="text-xs text-destructive">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password">비밀번호</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              {...register('password')}
            />
            {errors.password && (
              <p className="text-xs text-destructive">
                {errors.password.message}
              </p>
            )}
          </div>

          {formError && (
            <p className="text-sm text-destructive" role="alert">
              {formError}
            </p>
          )}

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting && <Spinner className="h-4 w-4" />}
            로그인
          </Button>
        </form>

        <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
          <span className="h-px flex-1 bg-border" />
          또는
          <span className="h-px flex-1 bg-border" />
        </div>

        <div className="space-y-2">
          {OAUTH.map((o) => (
            <a
              key={o.provider}
              href={`${authApi.oauthUrl(o.provider)}?redirect=${encodeURIComponent(redirect)}`}
              className={`flex h-10 w-full items-center justify-center rounded-md text-sm font-medium transition-colors ${o.cls}`}
            >
              {o.label}
            </a>
          ))}
        </div>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          아직 계정이 없으신가요?{' '}
          <Link to="/auth/register" className="font-medium text-primary hover:underline">
            회원가입
          </Link>
        </p>
        </div>
      </div>
    </div>
  );
}
